import { useQuery } from '@tanstack/react-query'
import { ApiError, hasLivePlayerMfa, playerFetch } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import { keys } from '@/api/hooks/shared/keys'
import { DEMO_PLAYER_MFA_SUMMARY, filterDemoUsers, findDemoUser } from './demo'
import type {
  PlayerMfaListQuery,
  PlayerMfaListResult,
  PlayerMfaSummary,
  PlayerMfaUser,
} from './types'

async function loadSummary(): Promise<PlayerMfaSummary> {
  if (!hasLivePlayerMfa()) return DEMO_PLAYER_MFA_SUMMARY
  try {
    return await playerFetch<PlayerMfaSummary>('/v1/admin/mfa/summary')
  } catch (err) {
    if (isUnreachableApiError(err) || (err instanceof ApiError && err.status === 503)) {
      return DEMO_PLAYER_MFA_SUMMARY
    }
    throw err
  }
}

async function loadUsers(query: PlayerMfaListQuery): Promise<PlayerMfaListResult> {
  if (!hasLivePlayerMfa()) return filterDemoUsers(query)
  const qs = new URLSearchParams()
  if (query.q) qs.set('q', query.q)
  if (query.mfa && query.mfa !== 'all') qs.set('mfa', query.mfa)
  if (query.method && query.method !== 'any') qs.set('method', query.method)
  if (query.limit != null) qs.set('limit', String(query.limit))
  if (query.offset != null) qs.set('offset', String(query.offset))
  const suffix = qs.toString() ? `?${qs}` : ''
  try {
    return await playerFetch<PlayerMfaListResult>(`/v1/admin/mfa/users${suffix}`)
  } catch (err) {
    if (isUnreachableApiError(err) || (err instanceof ApiError && err.status === 503)) {
      return filterDemoUsers(query)
    }
    throw err
  }
}

async function loadUser(idOrUsername: string): Promise<PlayerMfaUser | null> {
  if (!idOrUsername.trim()) return null
  if (!hasLivePlayerMfa()) return findDemoUser(idOrUsername)
  try {
    return await playerFetch<PlayerMfaUser>(
      `/v1/admin/mfa/users/${encodeURIComponent(idOrUsername.trim())}`,
    )
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null
    if (isUnreachableApiError(err) || (err instanceof ApiError && err.status === 503)) {
      return findDemoUser(idOrUsername)
    }
    throw err
  }
}

export function usePlayerMfaSummary() {
  return useQuery({
    queryKey: keys.playerMfaSummary,
    queryFn: loadSummary,
    staleTime: 30_000,
  })
}

export function usePlayerMfaUsers(query: PlayerMfaListQuery) {
  return useQuery({
    queryKey: keys.playerMfaUsers(query),
    queryFn: () => loadUsers(query),
    staleTime: 15_000,
  })
}

export function usePlayerMfaUser(idOrUsername: string | undefined, enabled = true) {
  const key = idOrUsername?.trim() || ''
  return useQuery({
    queryKey: keys.playerMfaUser(key),
    queryFn: () => loadUser(key),
    enabled: enabled && key.length > 0,
    staleTime: 30_000,
  })
}
